import { useEffect } from 'react'
import { createBrowserRouter, Outlet, redirect, RouterProvider } from 'react-router-dom'
import { Header } from './components/Header'
import { hasViewCharitiesPermission } from './services/permission.service'
import { CharitiesHomePage } from './components/pages/charities/charity-home/CharitiesHomePage'
import { CharityReviewPage } from './components/pages/charities/charities-review/CharityReviewPage'
import { CharityReviewProvider } from './components/contexts'
import { ErrorBoundary } from 'react-error-boundary'
import { GeneralErrorBoundary, onGeneralErrorBoundary, RouterErrorBoundary } from './components/ErrorPage'
import { initDatadog } from '@tomra/datadog-browser-logging'
import { DATADOG_CONFIG, ENVIRONMENT, STAGE, VERSION } from './lib'
import { CharityReportPage } from './components/pages/charities/charity-report/CharityReportPage'
import { CampaignListPage } from './components/pages/charities/campaign-list/CampaignListPage'
import { CharitiesListPage } from './components/pages/charities/charity-list/CharityListPage'
import { Container } from '@tomra/design-system-react/container'

const AuthenticatedAppLayout = () => {
  return (
    <>
      <Header />
      <Container>
        <Outlet />
      </Container>
    </>
  )
}

const PermissionDenied = () => {
  return (
    <div className="min-h-[400px] flex flex-col items-center justify-center p-xl text-center">
      <h1 className="text-xl font-bold mb-md">Access Denied</h1>
      <p className="text-md mb-lg">
        You do not have permission to view this application.
        <br />
        Please contact your administrator if you believe this is an error.
      </p>
    </div>
  )
}

export const App = () => {
  const charityRoutes = hasViewCharitiesPermission()
    ? [
        {
          path: 'charities',
          element: <Outlet />,
          children: [
            {
              path: '',
              element: <CharitiesHomePage />
            },
            {
              path: 'report',
              element: <CharityReportPage />
            },
            {
              path: 'list',
              element: <CharitiesListPage />
            },
            {
              path: ':charityId',
              element: (
                <CharityReviewProvider>
                  <CharityReviewPage />
                </CharityReviewProvider>
              )
            }
          ]
        },
        {
          path: 'campaigns',
          element: <Outlet />,
          children: [
            {
              path: 'list',
              element: <CampaignListPage />
            }
          ]
        }
      ]
    : []

  const authenticatedRoutes = [
    {
      element: <AuthenticatedAppLayout />,
      errorElement: <RouterErrorBoundary />,
      children: hasViewCharitiesPermission()
        ? [
            {
              path: '/',
              loader: () => redirect('/charities')
            },
            ...charityRoutes,
            {
              path: '*',
              loader: () => redirect('/charities')
            }
          ]
        : [
            {
              path: '*',
              element: <PermissionDenied />
            }
          ]
    }
  ]

  useEffect(() => {
    initDatadog({ ...DATADOG_CONFIG, version: VERSION, env: `${ENVIRONMENT}.${STAGE}` })
  }, [])

  return (
    <ErrorBoundary fallbackRender={GeneralErrorBoundary} onError={onGeneralErrorBoundary}>
      <RouterProvider router={createBrowserRouter(authenticatedRoutes)} />
    </ErrorBoundary>
  )
}
